<template>
  <div class="app-filter__inner">
    <div
      :class="[
        'app-filter__toggler',
        { 'app-filter__toggler_opened': filtersVisible },
      ]"
      @click="toggleFiltersVisibility"
    >
      Filter results
    </div>
    <div
      :class="[
        'app-filter__list',
        { 'app-filter__list_opened': filtersVisible },
      ]"
    >
      <h3 class="app-filter__title">Operation Type</h3>
      <v-select
        v-model="selectedCategoryName"
        :items="categoriesNames"
        :disabled="loading"
        append-icon="mdi-chevron-down"
      />

      <template v-if="Object.keys(form).length">
        <div v-for="(filterName, index) in filtersList" :key="index">
          <template v-if="filtersInfo[filterName].type === 'dropdown'">
            <v-select
              v-model="form[filtersInfo[filterName].slug]"
              :items="data[filtersInfo[filterName].slug]"
              :label="filterName"
              :disabled="
                data[filtersInfo[filterName].slug].length === 1 || loading
              "
              append-icon="mdi-chevron-down"
            />
          </template>

          <template v-if="filtersInfo[filterName].type === 'chips'">
            <h3 class="app-filter__title">{{ filterName }}</h3>
            <v-combobox
              v-model="form[filtersInfo[filterName].slug]"
              :items="data[filtersInfo[filterName].slug]"
              multiple
              small-chips
              :disabled="
                data[filtersInfo[filterName].slug].length === 1 || loading
              "
              append-icon="mdi-chevron-down"
            />
          </template>

          <template v-else-if="filtersInfo[filterName].type === 'checkbox'">
            <h3 class="app-filter__title">{{ filterName }}</h3>
            <v-layout wrap>
              <v-flex
                v-for="n in data[filtersInfo[filterName].slug]"
                :key="n + filterName"
                xs6
              >
                <v-checkbox
                  :disabled="
                    data[filtersInfo[filterName].slug].length === 1 || loading
                  "
                  :label="n"
                  :value="n"
                  v-model="form[filtersInfo[filterName].slug]"
                />
              </v-flex>
            </v-layout>
          </template>

          <template v-else-if="filtersInfo[filterName].type === 'slider'">
            <h3 class="app-filter__title">{{ filterName }}</h3>
            <div class="app-filter__slider">
              <v-text-field
                v-model="form[filtersInfo[filterName].slug][0]"
                class="app-filter__slider-inp"
                hide-details
                single-line
                type="number"
                :disabled="loading"
              />
              <v-range-slider
                class="app-filter__slider-range"
                v-model="form[filtersInfo[filterName].slug]"
                :max="data[filtersInfo[filterName].slug].max"
                :min="data[filtersInfo[filterName].slug].min"
                :step="5"
                :disabled="loading"
                track-color="rgba(0, 0, 0, 0.22)"
              />
              <v-text-field
                v-model="form[filtersInfo[filterName].slug][1]"
                class="app-filter__slider-inp"
                hide-details
                single-line
                type="number"
                :disabled="loading"
              />
            </div>
          </template>

          <template v-else-if="filtersInfo[filterName].type === 'toggle'">
            <v-switch
              :label="filterName"
              :disabled="
                data[filtersInfo[filterName].slug].length === 1 || loading
              "
              v-model="form[filtersInfo[filterName].slug]"
            />
          </template>
        </div>
        <v-btn
          v-if="selectedCategoryName"
          @click="refreshFilters"
          @mouseover="mouseoverReset(false)"
          @mouseout="mouseoverReset(true)"
          class="app-filter__reset"
          :disabled="loading"
          :depressed="isDepressed"
          large
        >
          Reset <v-icon>mdi-autorenew</v-icon>
        </v-btn>
      </template>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex'

function parseSliderVal(val) {
  const result = val.split('-').map(v => parseInt(v, 10))

  if (typeof result[0] !== 'number' || typeof result[1] !== 'number') {
    return [100, 100]
  }
  return result
}

const acfReducers = {
  array: (posts, snakedSlug) =>
    posts.reduce((uniqueValues, post) => {
      const { acf } = post
      const data = acf[snakedSlug]

      data.forEach(value => {
        if (uniqueValues.indexOf(value) === -1) {
          uniqueValues.push(value)
        }
      })

      return uniqueValues
    }, []),
  singleValue: (posts, snakedSlug) =>
    posts.reduce((uniqueValues, post) => {
      const { acf } = post
      const data = acf[snakedSlug]

      // Ignore empty string values
      if (uniqueValues.indexOf(data) === -1 && data !== '') {
        uniqueValues.push(data)
      }

      return uniqueValues.sort()
    }, []),
  label: (posts, snakedSlug) =>
    posts.reduce((uniqueValues, post) => {
      const { acf } = post
      const { label } = acf[snakedSlug]

      // Ignore 'N/A' labels
      if (uniqueValues.indexOf(label) === -1 && label !== 'N/A') {
        uniqueValues.push(label)
      }

      return uniqueValues
    }, []),
  slider: (posts, snakedSlug) => {
    const firstPost = posts.slice(0, 1)[0]
    const otherPosts = posts.slice(1)
    const [initMin, initMax] = parseSliderVal(firstPost.acf[snakedSlug])

    return otherPosts.reduce(
      (acc, post) => {
        const { acf } = post
        const data = acf[snakedSlug]
        const [postMin, postMax] = parseSliderVal(data)

        if (postMin < acc.min) {
          acc.min = postMin
        }

        if (postMax > acc.max) {
          acc.max = postMax
        }

        return acc
      },
      { min: initMin, max: initMax }
    )
  },
}

// Filtering rules
const checkFilterFns = {
  toggle: (postVal, filterVal) => {
    if (filterVal) {
      return postVal === filterVal
    }

    return true
  },
  slider: (postVal, filterVal) => {
    const [castedMinValue, castedMaxValue] = parseSliderVal(postVal)
    const [min, max] = filterVal

    return castedMinValue >= min && castedMaxValue <= max
  },
  cupSize: (postVal, filterVal) => {
    if (filterVal.length) {
      return filterVal.indexOf(postVal) !== -1
    }

    return true
  },
  implantShape: (postVal, filterVal) => {
    const { label } = postVal

    if (filterVal.length) {
      return filterVal.indexOf(label) !== -1
    }

    return true
  },
  condition: (postVal, filterVal) => {
    if (filterVal.length) {
      const filterValLength = filterVal.length

      for (let i = 0; i < filterValLength; i++) {
        if (postVal.indexOf(filterVal[i]) !== -1) {
          return true
        }
      }

      return false
    }

    return true
  },
  makeover: (postVal, filterVal) => {
    if (typeof filterVal === 'number') {
      return postVal.indexOf(filterVal) !== -1
    }

    return true
  },
}

export default {
  name: 'AppFilter',
  data() {
    return {
      isDepressed: true,
      filtersVisible: false, // affects only mobile screens
      firstLoadCompleted: false,
      selectedCategoryName: '',
      defaultCategoryName: 'Breast Augmentation',
      form: {},
      data: {},
      filtersList: [],
      // Hardcoded map structure
      catToFiltersMap: {
        'Mummy Makeover': [
          'Mummy Makeover Operations',
          'Massive weight loss',
          'Pregnancy',
          'Liposuction',
          'Muscle Repair',
        ],
        'Complex Breast Cases': ['Condition'],
        'Breast Augmentation': [
          'Final Cup Size',
          'Implant shape',
          'Implant volume',
        ],
        'Breast Augmentation + Mastopexy': [
          'Final Cup Size',
          'Implant shape',
          'Implant volume',
        ],
        'Breast Reduction': ['Before Cup Size', 'Final Cup Size'],
        'Breast Lift': ['Final Cup Size'],
        'Tummy Tuck': [
          'Massive weight loss',
          'Pregnancy',
          'Liposuction',
          'Muscle Repair',
        ],
        'Body Lift': [
          'Massive weight loss',
          'Pregnancy',
          'Liposuction',
          'Muscle Repair',
        ],
        'Thigh lift': [],
        'Arm lift': [],
      },
      // Hardcoded info about filters
      hardcodedFiltersInfo: {
        'Mummy Makeover Operations': {
          type: 'dropdown',
          data: [],
          isMakeover: 'Mummy Makeover',
          acfReducer() {
            return this.data
          },
          check: checkFilterFns.makeover,
        },
        Condition: {
          type: 'checkbox',
          data: [],
          isMakeover: '',
          acfReducer: acfReducers.singleValue,
          check: checkFilterFns.cupSize,
        },
        'Time of procedures': {
          type: 'checkbox',
          data: [],
          isMakeover: '',
          acfReducer: acfReducers.label,
          check: checkFilterFns.implantShape,
        },
        'Implant shape': {
          type: 'checkbox',
          data: [],
          isMakeover: '',
          acfReducer: acfReducers.label,
          check: checkFilterFns.implantShape,
        },
        'Before Cup Size': {
          type: 'chips',
          data: [],
          isMakeover: '',
          acfReducer: acfReducers.singleValue,
          check: checkFilterFns.cupSize,
        },
        'Final Cup Size': {
          type: 'chips',
          data: [],
          isMakeover: '',
          acfReducer: acfReducers.singleValue,
          check: checkFilterFns.cupSize,
        },
        'Implant volume': {
          type: 'slider',
          data: {
            min: 200,
            max: 550,
          },
          isMakeover: '',
          acfReducer: acfReducers.slider,
          check: checkFilterFns.slider,
        },
        'Massive weight loss': {
          type: 'toggle',
          // Wrap in array coz trying to reduce
          // amount of hidden classes in some runtimes
          data: [false],
          isMakeover: '',
          acfReducer: acfReducers.singleValue,
          check: checkFilterFns.toggle,
        },
        Pregnancy: {
          type: 'toggle',
          data: [false],
          isMakeover: '',
          acfReducer: acfReducers.singleValue,
          check: checkFilterFns.toggle,
        },
        Liposuction: {
          type: 'toggle',
          data: [false],
          isMakeover: '',
          acfReducer: acfReducers.singleValue,
          check: checkFilterFns.toggle,
        },
        'Muscle Repair': {
          type: 'toggle',
          data: [false],
          isMakeover: '',
          acfReducer: acfReducers.singleValue,
          check: checkFilterFns.toggle,
        },
      },
    }
  },
  watch: {
    appInitialized(isLoaded) {
      if (isLoaded) {
        this.applyDefaultCategory()
      }
    },
    selectedCategoryName(_, oldVal) {
      // Ignore first time loading
      if (oldVal !== '') {
        this.getPosts()
      }
    },
    form: {
      handler(form) {
        this.filterPosts(form)
      },
      deep: true,
    },
  },
  computed: {
    ...mapGetters(['posts', 'categories']),
    ...mapState(['loading', 'appInitialized']),
    rootCategories() {
      return this.categories.filter(c => c.parent === 0 && c.count)
    },
    subCategories() {
      return this.categories.filter(c => c.parent !== 0 && c.count)
    },

    categoriesInfo() {
      const { catToFiltersMap, rootCategories } = this

      // Combine hardcoded catToFiltersMap(info about associated filters)
      // with data from backend(name, id and slug fields for each category)
      return rootCategories.reduce((map, category) => {
        const { name, id, slug } = category
        const filterNames = catToFiltersMap[name]

        if (filterNames) {
          map[name] = {
            id,
            slug,
            filterNames,
          }
        }

        return map
      }, {})
    },
    filtersInfo() {
      const {
        hardcodedFiltersInfo,
        subCategories,
        categoriesInfo,
        objEach,
      } = this
      const map = {}

      objEach(hardcodedFiltersInfo, (filterName, filter) => {
        const { isMakeover } = filter
        const slug = this.kebabCase(filterName)

        // Makeover is a special case
        if (isMakeover) {
          const parentCat = categoriesInfo[isMakeover]

          if (parentCat) {
            const exclusiveParentId = categoriesInfo[isMakeover].id
            const data = subCategories
              .filter(sc => sc.parent === exclusiveParentId)
              .map(sc => sc.name)

            map[filterName] = Object.assign({ slug }, filter, { data })
          }
        } else {
          map[filterName] = Object.assign({ slug }, filter)
        }
      })

      return map
    },

    categoriesNames() {
      // List only those categories that
      // have both hardcoded data and backend data
      return Object.keys(this.categoriesInfo)
    },
    selectedCategory() {
      const { selectedCategoryName, categoriesInfo } = this

      return categoriesInfo[selectedCategoryName]
    },
  },
  methods: {
    ...mapActions({ getPostsAction: 'getPosts' }),
    ...mapMutations({ setFilteredPosts: 'SET_FILTERED_POSTS' }),
    mouseoverReset(bool) {
      this.isDepressed = bool
    },
    kebabCase(str) {
      return str.toLowerCase().replace(/[\s+]+/g, '-')
    },
    snakeCase(str) {
      return str.toLowerCase().replace(/[\s+-]+/g, '_')
    },
    objEach(obj, fn) {
      // Sugar function for object iteration
      for (let key in obj) {
        if (obj.hasOwnProperty(key)) {
          const value = obj[key]

          if (fn(key, value)) {
            break
          }
        }
      }
    },
    getCatBySlug(slug) {
      const { categoriesInfo, objEach } = this

      let cat
      let catName

      objEach(categoriesInfo, (key, value) => {
        if (value.slug === slug) {
          cat = value
          catName = key

          return true
        }
      })

      return { cat, catName }
    },
    getFilterBySlug(slug) {
      const { filtersInfo, objEach } = this

      let filter
      let filterName

      objEach(filtersInfo, (key, value) => {
        if (value.slug === slug) {
          filter = value
          filterName = key

          return true
        }
      })

      return { filter, filterName }
    },

    getVModelFor(filter) {
      const { type, data } = filter

      switch (type) {
        case 'slider': {
          const sliderData = this.data[filter.slug]

          return [sliderData.min, sliderData.max]
        }
        case 'toggle':
          return data[0]
        case 'checkbox':
          return []
        case 'chips':
          return []
        default:
          return ''
      }
    },
    normalizeForm(rawForm) {
      const { getFilterBySlug, objEach } = this
      const normalizedForm = {}
      let normalizedValue

      objEach(rawForm, (slug, value) => {
        const { type, data } = getFilterBySlug(slug).filter

        switch (type) {
          case 'slider': {
            // Trying to make "123,321" -> [123,321]
            if (value instanceof Array) {
              normalizedValue = value
            } else {
              const { min: availMin, max: availMax } = data
              const splitted = value.split(',').map(v => parseInt(v, 10))
              const castedMin = splitted[0] < availMin ? availMin : splitted[0]
              const castedMax = splitted[1] > availMax ? availMax : splitted[1]

              normalizedValue = [castedMin, castedMax]
            }

            break
          }
          case 'toggle': {
            // Trying to make "false" -> false
            try {
              normalizedValue = Boolean(JSON.parse(value))
            } catch (e) {
              normalizedValue = data[0]
            }

            break
          }
          case 'checkbox': {
            // Trying to make "1,2,3" -> ["1","2","3"]
            normalizedValue = value
              .split(',')
              .filter(v => data.indexOf(v) !== -1)

            break
          }
          case 'chips': {
            // Also trying to make "1,2,3" -> ["1","2","3"]
            normalizedValue = value
              .split(',')
              .filter(v => data.indexOf(v) !== -1)

            break
          }
          default:
            normalizedValue = value
        }

        normalizedForm[slug] = normalizedValue
      })

      return normalizedForm
    },
    applyValuesFromQuery(newForm) {
      // We are searching for any slugs in query params.
      // Assumed query format: ?operation=<category slug>[&<filter slug>=<fliter value>]
      const { getCatBySlug, objEach } = this
      const { query } = this.$route
      let foundCategoryName

      this.firstLoadCompleted = true

      // We are looking for category name with a given slug
      if ('operation' in query) {
        foundCategoryName = getCatBySlug(query.operation).catName
      }

      // Continue only if 'operation' query was provided
      if (foundCategoryName) {
        // We search values in the query
        // for each filter in the form
        objEach(newForm, filterSlug => {
          if (filterSlug in query) {
            newForm[filterSlug] = query[filterSlug]
          }
        })

        // And sanitize resulted form coz
        // we mustn't trust any user input
        return this.normalizeForm(newForm)
      }

      return newForm
    },
    applyDefaultCategory() {
      // We are searching for any slugs in query params.
      // Assumed query format: ?operation=<category slug>
      const { categoriesInfo, defaultCategoryName, getCatBySlug } = this
      const { query } = this.$route
      let foundCategoryName

      // We are looking for category name with a given slug
      if ('operation' in query) {
        foundCategoryName = getCatBySlug(query.operation).catName
      }

      let resultedDefaultCatName

      if (foundCategoryName) {
        resultedDefaultCatName = foundCategoryName
      }
      // Check whether the default category
      // name is listed in category map
      else if (defaultCategoryName in categoriesInfo) {
        resultedDefaultCatName = defaultCategoryName
      } else {
        // Otherwise we will load the first existing category
        resultedDefaultCatName = Object.keys(categoriesInfo)[0] || ''
      }

      this.selectedCategoryName = resultedDefaultCatName

      return this.getPosts()
    },
    getNewData() {
      const { posts, selectedCategory, filtersInfo, snakeCase } = this
      const catFilterNames = selectedCategory.filterNames

      return catFilterNames.reduce((acc, filterName) => {
        const filter = filtersInfo[filterName]
        const { type } = filter
        // For some reasons acf uses a snake case for slug
        const snakedSlug = snakeCase(filter.slug)
        // All filters have a reducer function
        // that knows how to combine values
        // through all the posts in current category
        const conjunction = filter.acfReducer(posts, snakedSlug)

        // Rid off checkboxes and chips
        // that hasn't any data
        if (type === 'checkbox' && type === 'chips') {
          if (conjunction.length) {
            acc[filter.slug] = conjunction
          }
        } else {
          acc[filter.slug] = conjunction
        }

        return acc
      }, {})
    },
    getNewForm() {
      // This method constructs dynamic
      // v-models for filter fields
      const { objEach, data, getFilterBySlug } = this
      const newForm = {}

      objEach(data, filterSlug => {
        const { filter } = getFilterBySlug(filterSlug)

        newForm[filterSlug] = this.getVModelFor(filter)
      })

      return newForm
    },
    refreshFilters() {
      const { getFilterBySlug } = this
      const newData = this.getNewData()
      const newFiltersList = []

      // 1 Update data
      this.data = newData

      let newForm = this.getNewForm()

      if (!this.firstLoadCompleted) {
        newForm = this.applyValuesFromQuery(newForm)
      }

      // Restrict form values by available data
      this.objEach(newForm, (slug, value) => {
        const {
          filter: { type },
          filterName,
        } = getFilterBySlug(slug)
        const associatedData = newData[slug]
        let newValue

        if (type === 'checkbox' || type === 'chips') {
          // Choose only available values
          const onlyAvailableValues = value.filter(
            e => associatedData.indexOf(e) !== -1
          )

          if (associatedData.length === 1) {
            // If there is only one value available
            // (ex.: ['C']) then we choose it
            newValue = [].concat(associatedData)
          } else {
            newValue = onlyAvailableValues
          }
        } else if (type === 'toggle' && associatedData.length === 1) {
          newValue = associatedData[0]
        } else if (type === 'dropdown') {
          if (associatedData.length === 1) {
            newValue = associatedData[0]
          } else if (value !== '' && associatedData.indexOf(value) !== -1) {
            newValue = associatedData[0] || ''
          } else {
            newValue = value
          }
        } else {
          newValue = value
        }

        newForm[slug] = newValue
        newFiltersList.push(filterName)
      })

      // 2 Update form
      this.form = newForm
      // 3 Update filters list
      this.filtersList = newFiltersList
    },

    getPosts() {
      const { selectedCategory } = this

      if (selectedCategory) {
        const catId = selectedCategory.id

        return this.getPostsAction(catId).then(this.refreshFilters)
      }
    },

    filterPosts(form) {
      const { posts, subCategories, snakeCase, getFilterBySlug, objEach } = this
      const filteredPosts = []

      posts.forEach(post => {
        const passedList = []

        objEach(form, (slug, filterVal) => {
          const { filter, filterName } = getFilterBySlug(slug)
          const snakedCase = snakeCase(filter.slug)

          // Makeover is a special case
          if (filterName === 'Mummy Makeover Operations') {
            let makeoverCatId = 27

            // Find makeover cat by name
            subCategories.forEach(sc => {
              if (sc.name === filterVal) {
                makeoverCatId = sc.id
              }
            })

            const postVal = post.results_gallery_categories
            const passed = filter.check(postVal, makeoverCatId)

            passedList.push(passed)
          } else {
            const postVal = post.acf[snakedCase]
            const passed = filter.check(postVal, filterVal)

            passedList.push(passed)
          }
        })

        const allPassed = passedList.reduce((acc, p) => acc && p, true)

        if (allPassed) {
          filteredPosts.push(post)
        }
      })

      this.setFilteredPosts(filteredPosts)
    },

    toggleFiltersVisibility() {
      this.filtersVisible = !this.filtersVisible
    },
  },
}
</script>

<style lang="scss">
.v-slider {
  .v-slider__thumb {
    width: 48px;
    height: 48px;
    left: -24px;
  }

  .v-slider__thumb-container {
    .v-slider__thumb {
      width: 24px;
      height: 24px;
      left: -12px;
      cursor: pointer;

      transform: translateY(-50%) scale(1) !important;
      &:before {
        left: -6px;
        top: -6px;
      }
    }

    &:before {
      width: 50px;
      height: 50px;
      left: -25px;
      top: -25px;
    }

    &--is-active {
      &:before {
        opacity: 0.2;
        transform: scale(1);
      }
    }
  }
}

.app-filter__toggler {
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
  position: relative;

  @media (min-width: 960px) {
    display: none;
  }

  &:after {
    font-family: 'Material Icons';
    font-size: 26px;
    color: #879ba1;
    transform: translateY(-50%);
    position: absolute;
    top: 50%;
    right: 36px;
    content: 'expand_more';
  }

  &_opened:after {
    content: 'close';
  }
}

.app-filter__list {
  @media (max-width: 960px) {
    position: absolute;
    left: 0;
    top: 100%;
    transform: translateY(0);
    background-color: #eaebeb;
    z-index: 2;
    padding-left: 15px;
    padding-right: 15px;
    width: 100%;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.25s ease-in-out, visibility 0.25s ease-in-out,
      transform 0.25s ease-in-out;
    contain: content;
    padding-bottom: 60px;

    &_opened {
      transform: translateY(20px);
      opacity: 1;
      visibility: visible;
    }
  }
}

.app-filter__inner {
  position: relative;
  width: 100%;
  padding-right: 15px;
  padding-left: 15px;
  margin: 0;

  @media (min-width: 960px) {
    padding-right: 0;
    padding-left: 0;
    margin-right: 10% !important;
    margin-left: 10% !important;
  }
}

.app-filter__title {
  text-align: left;
  font-size: 15px;
  color: #a5a6a6;
}

.app-filter__slider {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.app-filter__slider-range {
  margin-top: 21px;
  flex-basis: 100% !important;
}

.app-filter__slider-inp {
  max-width: 40px !important;
  flex-shrink: 0 !important;
  margin-top: 0 !important;
  padding-top: 0 !important;

  .v-input__slot:after,
  .v-input__slot:before {
    display: none;
  }

  &:first-child {
    margin-right: 20px;
    input {
      text-align: left;
    }
  }

  &:last-child {
    margin-left: 20px;
    input {
      text-align: right;
    }
  }
}

button.app-filter__reset {
  width: 240px;
  margin: 60px auto 0 auto;
  display: block;
  .v-icon {
    font-size: 16px;
    margin-left: 5px;
  }
}
</style>
